import type { MarketVariants } from '@/lib/i18n'

export type Slide7FollowBreadcrumbsCopy = {
  /** Phishing SMS mockup. On the Qatar face the sender is a generic bank and
   *  the PayPal mark is replaced with a neutral bank mark (see the .tsx). */
  senderName: string
  senderChannel: string
  now: string
  /** SMS body split around the underlined link: "{before}{link}{after}". */
  smsBefore: string
  smsLink: string
  smsAfter: string

  title: string
  body: string
  guideButton: string

  /** Practical guide slideover. */
  guideTitle: string
  closeAria: string
  intro: string
  bankHeading: string
  bankBody: string
  deliveryHeading: string
  deliveryBody: string
  tipsHeading: string
  tips: string[]
}

export const copy: MarketVariants<Slide7FollowBreadcrumbsCopy> = {
  US: {
    senderName: 'PayPal',
    senderChannel: 'Text message',
    now: 'now',
    smsBefore: 'We noticed unusual activity on your PayPal account. ',
    smsLink: 'Confirm your details',
    smsAfter: ' now to avoid account limitation.',

    title: 'Ignore the link. Open the app.',
    body:
      'Assume any link in a message or email could be a trap. When it involves money, accounts, or personal details, open the real app or type the website address yourself instead.',
    guideButton: 'Practical guide',

    guideTitle: 'The official app habit',
    closeAria: 'Close',
    intro:
      'Scam links are designed to rush you onto fake websites or login pages. Opening the real app or typing the website address yourself keeps you in control. Simply put, never log into an account through a link in a message.',
    bankHeading: 'Got an email from your bank?',
    bankBody:
      'Don’t click the link. Open the banking app or call the number on the back of your card. Either will show you if there’s a real problem. If there’s nothing there, treat the message as a scam.',
    deliveryHeading: 'Got a text about a delivery?',
    deliveryBody:
      'Don’t tap the tracking link. Go to the delivery company’s website or app yourself and enter the tracking number there. Fake delivery texts are one of the most common modern scams.',
    tipsHeading: 'Tips',
    tips: [
      'Bookmark the sites you use most to avoid typos and fake lookalikes.',
      'Only download apps from the official App Store or Google Play, never from a link.',
      'Pin your key apps to the home screen so they’re always one tap away.',
    ],
  },
  QA: {
    // The locally recognizable shape: "your card will be blocked, update
    // your details" from "your bank". No real bank is named.
    senderName: 'Your bank',
    smsBefore: 'We noticed unusual activity on your account. ',
    smsAfter: ' now or your card will be blocked.',
    deliveryBody:
      'Don’t tap the tracking link. Go to the delivery company’s website or app yourself, whether that’s Qatar Post or a courier, and enter the tracking number there. Fake delivery texts are one of the most common modern scams.',
  },
  ar: {
    senderName: 'بنكك',
    senderChannel: 'رسالة نصية',
    now: 'الآن',
    smsBefore: 'لاحظنا نشاطاً غير معتاد على حسابك. ',
    smsLink: 'أكّد بياناتك',
    smsAfter: ' الآن وإلا سيتم إيقاف بطاقتك.',

    title: 'تجاهل الرابط. افتح التطبيق.',
    body:
      'افترض أن أي رابط في رسالة نصية أو بريد إلكتروني قد يكون فخاً. عندما يتعلق الأمر بالمال أو الحسابات أو البيانات الشخصية، افتح التطبيق الرسمي أو اكتب عنوان الموقع بنفسك بدلاً من ذلك.',
    guideButton: 'دليل عملي',

    guideTitle: 'عادة استخدام التطبيق الرسمي',
    closeAria: 'إغلاق',
    intro:
      'روابط الاحتيال مصمّمة لدفعك بسرعة إلى مواقع أو صفحات تسجيل دخول مزيفة. أما فتح التطبيق الرسمي أو كتابة عنوان الموقع بنفسك فيُبقي زمام الأمر بيدك. ببساطة: لا تسجّل الدخول إلى أي حساب عبر رابط في رسالة.',
    bankHeading: 'وصلك بريد إلكتروني من البنك؟',
    bankBody:
      'لا تضغط على الرابط. افتح تطبيق البنك أو اتصل بالرقم المدوّن خلف بطاقتك. كلاهما سيُظهر لك إن كانت هناك مشكلة حقيقية. وإن لم تجد شيئاً، فتعامل مع الرسالة على أنها احتيال.',
    deliveryHeading: 'وصلتك رسالة نصية عن طرد؟',
    deliveryBody:
      'لا تضغط على رابط التتبع. ادخل بنفسك إلى موقع شركة التوصيل أو تطبيقها، سواء كان بريد قطر أو شركة شحن أخرى، وأدخل رقم التتبع هناك. رسائل التوصيل المزيفة من أكثر عمليات الاحتيال شيوعاً اليوم.',
    tipsHeading: 'نصائح',
    tips: [
      'احفظ المواقع التي تستخدمها كثيراً في المفضلة لتجنّب الأخطاء الإملائية والمواقع المقلّدة.',
      'حمّل التطبيقات من App Store أو Google Play الرسميين فقط، وليس من رابط أبداً.',
      'ثبّت تطبيقاتك الأساسية على الشاشة الرئيسية لتكون دائماً على بُعد نقرة واحدة.',
    ],
  },
}
